import { INK, PALETTE, SHADE, WORLD } from '@blorbs/common';
import type { Camera } from './camera';
import { circle, hash } from './art';

const LAVA = PALETTE[2]; // coral
const LAVA_DARK = SHADE[LAVA]!;
const LAVA_HOT = PALETTE[3]; // orange
const LAVA_GLOW = PALETTE[4]; // butter

interface Blob {
  x: number;
  y: number;
  r: number;
  phase: number;
  speed: number;
  hot: boolean;
}

// Blobs cover well beyond the world so wide screens never see bare lava.
const SPREAD = 900;
const BLOBS: Blob[] = Array.from({ length: 140 }, (_, i) => ({
  x: -SPREAD + hash(i * 4 + 1) * (WORLD.size + SPREAD * 2),
  y: -SPREAD + hash(i * 4 + 2) * (WORLD.size + SPREAD * 2),
  r: 28 + hash(i * 4 + 3) * 70,
  phase: hash(i * 4 + 4) * Math.PI * 2,
  speed: 0.3 + hash(i * 4 + 5) * 0.5,
  hot: hash(i * 4 + 6) < 0.3,
}));

export function drawLava(ctx: CanvasRenderingContext2D, cam: Camera, islandRadius: number, time: number): void {
  const b = cam.worldBounds();
  ctx.fillStyle = LAVA;
  ctx.fillRect(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0);

  // Slow drifting crust + hot spots.
  for (const blob of BLOBS) {
    if (blob.x + blob.r < b.x0 || blob.x - blob.r > b.x1 || blob.y + blob.r < b.y0 || blob.y - blob.r > b.y1) continue;
    const wob = Math.sin(time * blob.speed + blob.phase);
    const x = blob.x + wob * 12;
    const y = blob.y + Math.cos(time * blob.speed * 0.8 + blob.phase) * 9;
    const r = blob.r * (1 + wob * 0.06);
    ctx.fillStyle = blob.hot ? LAVA_HOT : LAVA_DARK;
    ctx.globalAlpha = blob.hot ? 0.55 : 0.5;
    circle(ctx, x, y, r);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Heat halo hugging the island edge.
  const g = ctx.createRadialGradient(WORLD.cx, WORLD.cy, islandRadius, WORLD.cx, WORLD.cy, islandRadius + 140);
  g.addColorStop(0, hexA(LAVA_GLOW, 0.55));
  g.addColorStop(0.5, hexA(LAVA_HOT, 0.25));
  g.addColorStop(1, hexA(LAVA_HOT, 0));
  ctx.fillStyle = g;
  circle(ctx, WORLD.cx, WORLD.cy, islandRadius + 140);
  ctx.fill();

  // Faint ink ripples so the lava reads as drawn, not flat.
  ctx.strokeStyle = INK;
  ctx.globalAlpha = 0.12;
  ctx.lineWidth = Math.max(2, 1.5 / cam.scale);
  for (let i = 0; i < 26; i++) {
    const x = -SPREAD + hash(i * 7 + 11) * (WORLD.size + SPREAD * 2);
    const y = -SPREAD + hash(i * 7 + 12) * (WORLD.size + SPREAD * 2);
    if (x < b.x0 - 80 || x > b.x1 + 80 || y < b.y0 - 40 || y > b.y1 + 40) continue;
    const w = 30 + hash(i * 7 + 13) * 50;
    const drift = Math.sin(time * 0.6 + i) * 6;
    ctx.beginPath();
    ctx.moveTo(x - w, y + drift);
    ctx.quadraticCurveTo(x - w / 2, y - 8 + drift, x, y + drift);
    ctx.quadraticCurveTo(x + w / 2, y + 8 + drift, x + w, y + drift);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

export function hexA(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}
